<template>
  <v-dialog v-model="dialog" max-width="1200" @click:outside="close">
    <v-card>
      <v-card-title class="d-flex justify-space-between align-center">
        <div>
          <h3>Historique de surveillance - {{ monitor.title }}</h3>
          <div class="text-caption text--secondary">
            {{ monitor.url }}
          </div>
        </div>
        <v-btn icon @click="close">
          <v-icon>{{ icons.mdiClose }}</v-icon>
        </v-btn>
      </v-card-title>

      <v-divider />

      <!-- Date Filter -->
      <v-card-text class="pa-4 pb-2">
        <v-row>
          <v-col cols="12" md="6">
            <v-menu
              v-model="dateRangeMenu"
              :close-on-content-click="false"
              transition="scale-transition"
              offset-y
              min-width="290px"
            >
              <template v-slot:activator="{ on, attrs }">
                <v-text-field
                  v-model="dateRangeText"
                  label="Période"
                  prepend-inner-icon="mdi-calendar"
                  readonly
                  outlined
                  dense
                  v-bind="attrs"
                  v-on="on"
                  clearable
                  @click:clear="clearDates"
                />
              </template>
              <v-date-picker
                v-model="dates"
                range
                locale="fr-FR"
                first-day-of-week="1"
                :max="today"
                no-title
                scrollable
                @change="onDateChange"
              />
            </v-menu>
          </v-col>
          <v-col cols="12" md="2">
            <v-btn
              color="primary"
              block
              @click="applyDateFilter"
              :loading="loading"
              :disabled="!hasValidDateRange"
            >
              Appliquer
            </v-btn>
          </v-col>
        </v-row>
      </v-card-text>

      <div v-if="chartData.length > 0 && !loading" class="ma-3">
        Données du {{ dataRangeInfo }}
      </div>
      <v-divider />

      <v-card-text class="pa-4 pt-2">
        <div v-if="loading" class="text-center py-8">
          <v-progress-circular indeterminate color="primary" />
          <div class="mt-3">Chargement des données...</div>
        </div>

        <div v-else-if="error" class="text-center py-8">
          <v-icon large color="error">{{ icons.mdiAlertCircle }}</v-icon>
          <div class="mt-3 error--text">{{ error }}</div>
        </div>

        <div v-else-if="chartData.length === 0" class="text-center py-8">
          <v-icon large color="grey">{{ icons.mdiChartLine }}</v-icon>
          <div class="mt-3 text--secondary">Aucune donnée disponible</div>
        </div>

        <div v-else>
          <!-- Summary Stats -->
          <v-row class="mb-4">
            <v-col cols="3">
              <v-card outlined>
                <v-card-text class="text-center">
                  <div class="text-h5 font-weight-bold" :class="avgTtfbClass">
                    {{ avgTtfb }}ms
                  </div>
                  <div class="text-caption">TTFB moyen</div>
                </v-card-text>
              </v-card>
            </v-col>
            <v-col cols="3">
              <v-card outlined>
                <v-card-text class="text-center">
                  <div
                    class="text-h5 font-weight-bold"
                    :class="successRateClass"
                  >
                    {{ successRate }}%
                  </div>
                  <div class="text-caption">Taux de succès</div>
                </v-card-text>
              </v-card>
            </v-col>
            <v-col cols="3">
              <v-card outlined>
                <v-card-text class="text-center">
                  <div class="text-h5 font-weight-bold">
                    {{ totalChecks }}
                  </div>
                  <div class="text-caption">Vérifications</div>
                </v-card-text>
              </v-card>
            </v-col>
            <v-col cols="3">
              <v-card outlined>
                <v-card-text class="text-center">
                  <div class="text-h5 font-weight-bold">
                    {{ monitor.interval_minutes }} min
                  </div>
                  <div class="text-caption">Intervalle</div>
                </v-card-text>
              </v-card>
            </v-col>
          </v-row>

          <!-- Chart -->
          <v-card outlined>
            <v-card-text>
              <vue-apex-charts
                :height="400"
                :options="chartOptions"
                :series="series"
              />
            </v-card-text>
          </v-card>
        </div>
      </v-card-text>
    </v-card>
  </v-dialog>
</template>

<script>
import useAxios from "@/hooks/useAxios";
import { mdiAlertCircle, mdiChartLine, mdiClose } from "@mdi/js";
import VueApexCharts from "vue-apexcharts";

export default {
  name: "UrlMonitorHistoryModal",
  components: {
    VueApexCharts,
  },
  props: {
    monitor: {
      type: Object,
      required: true,
    },
  },
  setup() {
    const { axiosGet } = useAxios();

    return {
      axiosGet,
    };
  },
  data() {
    const today = new Date().toISOString().substr(0, 10);
    return {
      dialog: true,
      loading: true,
      error: null,
      historyData: null,
      dates: [today, today],
      dateRangeMenu: false,
      icons: {
        mdiClose,
        mdiAlertCircle,
        mdiChartLine,
      },
    };
  },
  computed: {
    chartData() {
      if (!this.historyData || !this.historyData.data) return [];
      return this.historyData.data;
    },
    series() {
      if (!this.chartData.length) return [];

      return [
        {
          name: "TTFB (ms)",
          data: this.chartData.map((item) => ({
            x: new Date(item.timestamp).getTime(),
            y: item.ttfb_ms || null,
          })),
        },
        {
          name: "Taille de page (KB)",
          data: this.chartData.map((item) => ({
            x: new Date(item.timestamp).getTime(),
            y: item.size_bytes ? Math.round(item.size_bytes / 1024) : null,
          })),
        },
        {
          name: "Éléments DOM",
          data: this.chartData.map((item) => ({
            x: new Date(item.timestamp).getTime(),
            y: item.dom_elements_count || null,
          })),
        },
      ];
    },
    errorAnnotations() {
      if (!this.chartData.length) return [];

      // Get all errors (non-200 status codes)
      return this.chartData
        .filter((item) => item.status_code !== 200 && item.status_code !== null)
        .map((item) => ({
          x: new Date(item.timestamp).getTime(),
          borderColor: "#f44336",
          label: {
            borderColor: "#f44336",
            style: {
              color: "#fff",
              background: "#f44336",
              fontSize: "11px",
            },
            text: `${item.status_code}`,
            orientation: "vertical",
            position: "top",
          },
        }));
    },
    chartOptions() {
      return {
        chart: {
          id: "ttfb-history",
          height: 400,
          type: "line",
          zoom: {
            enabled: true,
            type: "x",
            autoScaleYaxis: true,
          },
          toolbar: {
            show: true,
          },
          animations: {
            enabled: false,
          },
          events: {
            mounted: (chart) => {
              // Hide page size and DOM count series by default
              chart.hideSeries("Taille de page (KB)");
              chart.hideSeries("Éléments DOM");
            },
          },
        },
        colors: ["#1976D2", "#FF9800", "#4CAF50"],
        dataLabels: {
          enabled: false,
        },
        stroke: {
          curve: "smooth",
          width: 2,
        },
        markers: {
          size: 0,
          hover: {
            size: 5,
            sizeOffset: 3,
          },
        },
        xaxis: {
          type: "datetime",
          labels: {
            datetimeUTC: false,
            datetimeFormatter: {
              year: "yyyy",
              month: "MMM 'yy",
              day: "dd MMM",
              hour: "HH:mm",
            },
          },
          title: {
            text: "Date et heure",
          },
        },
        yaxis: [
          {
            seriesName: "TTFB (ms)",
            title: {
              text: "TTFB (ms)",
              style: {
                color: "#1976D2",
              },
            },
            labels: {
              style: {
                colors: "#1976D2",
              },
            },
            min: 0,
          },
          {
            seriesName: "Taille de page (KB)",
            opposite: true,
            title: {
              text: "Taille de page (KB)",
              style: {
                color: "#FF9800",
              },
            },
            labels: {
              style: {
                colors: "#FF9800",
              },
            },
            min: 0,
          },
          {
            seriesName: "Éléments DOM",
            opposite: true,
            show: false,
          },
        ],
        tooltip: {
          x: {
            format: "dd/MM/yyyy HH:mm:ss",
          },
          y: [
            {
              formatter: (value) => {
                if (value === null) return "N/A";
                return `${value}ms`;
              },
            },
            {
              formatter: (value) => {
                if (value === null) return "N/A";
                return `${value} KB`;
              },
            },
            {
              formatter: (value) => {
                if (value === null) return "N/A";
                return `${value} éléments`;
              },
            },
          ],
        },
        grid: {
          borderColor: "#e0e0e0",
          strokeDashArray: 5,
        },
        legend: {
          show: true,
          position: "top",
          horizontalAlign: "left",
          onItemClick: {
            toggleDataSeries: true,
          },
          onItemHover: {
            highlightDataSeries: true,
          },
        },
        annotations: {
          xaxis: this.errorAnnotations,
          yaxis: [
            {
              y: 800,
              borderColor: "#FF9800",
              strokeDashArray: 5,
              label: {
                borderColor: "#FF9800",
                style: {
                  color: "#fff",
                  background: "#FF9800",
                },
                text: "Seuil d'alerte (800ms)",
              },
            },
          ],
        },
      };
    },
    avgTtfb() {
      if (!this.chartData.length) return 0;
      const validData = this.chartData.filter((item) => item.ttfb_ms !== null);
      if (!validData.length) return 0;
      const sum = validData.reduce((acc, item) => acc + item.ttfb_ms, 0);
      return Math.round(sum / validData.length);
    },
    avgTtfbClass() {
      if (this.avgTtfb > 800) return "warning--text";
      return "success--text";
    },
    successRate() {
      if (!this.chartData.length) return 0;
      const successCount = this.chartData.filter((item) => item.success).length;
      const errorCount = this.chartData.filter((item) => !item.success).length;
      console.log(`Success rate: ${successCount}/${this.chartData.length} (${errorCount} errors)`);
      return Math.round((successCount / this.chartData.length) * 100);
    },
    successRateClass() {
      if (this.successRate < 90) return "error--text";
      if (this.successRate < 99) return "warning--text";
      return "success--text";
    },
    totalChecks() {
      return this.historyData?.total_checks || 0;
    },
    dataRangeInfo() {
      if (!this.chartData.length) return "";

      const firstDate = new Date(this.chartData[0].timestamp);
      const lastDate = new Date(
        this.chartData[this.chartData.length - 1].timestamp
      );

      // Format dates
      const formatDate = (date) => {
        return date.toLocaleDateString("fr-FR", {
          day: "numeric",
          month: "short",
          hour: "2-digit",
          minute: "2-digit",
        });
      };

      // If same day, show time range
      if (firstDate.toDateString() === lastDate.toDateString()) {
        const timeFormatter = new Intl.DateTimeFormat("fr-FR", {
          hour: "2-digit",
          minute: "2-digit",
        });
        return `${firstDate.toLocaleDateString("fr-FR", {
          day: "numeric",
          month: "long",
          year: "numeric",
        })} (${timeFormatter.format(firstDate)} - ${timeFormatter.format(
          lastDate
        )})`;
      }

      // Different days
      return `${formatDate(firstDate)} au ${formatDate(lastDate)}`;
    },
    today() {
      return new Date().toISOString().substr(0, 10);
    },
    dateRangeText() {
      if (this.dates.length === 2) {
        return `${this.dates[0]} => ${this.dates[1]}`;
      }
      return "";
    },
    hasValidDateRange() {
      return this.dates.length === 2;
    },
    startDate() {
      return this.dates[0] || null;
    },
    endDate() {
      return this.dates[1] || null;
    },
  },
  async mounted() {
    await this.loadHistory();
  },
  methods: {
    async loadHistory() {
      try {
        this.loading = true;
        this.error = null;

        // Build query params
        const params = {};
        if (this.startDate) {
          params.start_date = `${this.startDate}T00:00:00`;
        }
        if (this.endDate) {
          params.end_date = `${this.endDate}T23:59:59`;
        }

        const response = await this.axiosGet(
          `/url-monitors/${this.monitor.id}/extended-history`,
          params
        );

        this.historyData = response.data;
        
        // Debug: Check data sample
        if (this.historyData.data && this.historyData.data.length > 0) {
          console.log('Data sample:', this.historyData.data.slice(0, 5));
          const nonSuccessItems = this.historyData.data.filter(item => !item.success);
          if (nonSuccessItems.length > 0) {
            console.log('Non-success items:', nonSuccessItems.slice(0, 3));
          }
        }
      } catch (err) {
        this.error = "Erreur lors du chargement de l'historique";
      } finally {
        this.loading = false;
      }
    },
    applyDateFilter() {
      this.loadHistory();
    },
    clearDates() {
      this.dates = [];
      this.loadHistory();
    },
    onDateChange() {
      // Sort dates if needed
      if (this.dates.length === 2 && this.dates[0] > this.dates[1]) {
        this.dates = [this.dates[1], this.dates[0]];
      }
    },
    close() {
      this.$emit("close");
    },
  },
};
</script>

<style scoped>
.v-dialog {
  overflow-y: auto;
}
</style>
